import React from 'react';
import { useForm } from 'react-hook-form';

const PlayerForm = ({ player, teams, onSubmit, onCancel, isLoading }) => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: player || {}
  });

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 border border-gray-200">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
        {player ? 'Edit Player' : 'Create New Player'}
      </h2>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              {...register('name', { required: 'Player name is required' })}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
              placeholder="Enter player name"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Age *
            </label>
            <input
              {...register('age', { 
                required: 'Age is required',
                min: { value: 16, message: 'Age must be at least 16' },
                max: { value: 50, message: 'Age must be at most 50' }
              })}
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
              placeholder="Enter age"
            />
            {errors.age && (
              <p className="text-red-500 text-sm mt-1">{errors.age.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nationality *
            </label>
            <input
              {...register('nationality', { required: 'Nationality is required' })}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
              placeholder="Enter nationality"
            />
            {errors.nationality && (
              <p className="text-red-500 text-sm mt-1">{errors.nationality.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Jersey Number
            </label>
            <input
              {...register('jersey_number', { 
                min: { value: 1, message: 'Jersey number must be at least 1' },
                max: { value: 99, message: 'Jersey number must be at most 99' }
              })}
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
              placeholder="Enter jersey number"
            />
            {errors.jersey_number && (
              <p className="text-red-500 text-sm mt-1">{errors.jersey_number.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              {...register('role', { required: 'Role is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select role</option>
              <option value="batsman">Batsman</option>
              <option value="bowler">Bowler</option>
              <option value="all_rounder">All Rounder</option>
              <option value="wicket_keeper">Wicket Keeper</option>
            </select>
            {errors.role && (
              <p className="text-red-500 text-sm mt-1">{errors.role.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Team
            </label>
            <select
              {...register('team_id')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select team</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Batting Style *
            </label>
            <select
              {...register('batting_style', { required: 'Batting style is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select batting style</option>
              <option value="right_handed">Right Handed</option>
              <option value="left_handed">Left Handed</option>
            </select>
            {errors.batting_style && (
              <p className="text-red-500 text-sm mt-1">{errors.batting_style.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bowling Style
            </label>
            <select
              {...register('bowling_style')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select bowling style</option>
              <option value="right_arm_fast">Right Arm Fast</option>
              <option value="left_arm_fast">Left Arm Fast</option>
              <option value="right_arm_medium">Right Arm Medium</option>
              <option value="left_arm_medium">Left Arm Medium</option>
              <option value="right_arm_spin">Right Arm Spin</option>
              <option value="left_arm_spin">Left Arm Spin</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Profile Image URL
          </label>
          <input
            {...register('profile_image_url')}
            type="url"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            placeholder="https://example.com/profile.jpg"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:space-x-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="order-2 sm:order-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="order-1 sm:order-2 px-4 py-2 bg-cricket-green text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : (player ? 'Update Player' : 'Create Player')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlayerForm;
